import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useRef, useState } from 'react';
import { api } from '../api';
import type { Runner } from '../types';
import { liveKey, runnersKey } from './queries';

/**
 * Add one runner: late and race-day registrations, walk-ups, bib swaps.
 * `stacked` fits a side column (race day); the default lays fields out in a row (roster).
 */
export function AddRunnerForm({ raceId, stacked = false }: { raceId: string; stacked?: boolean }) {
  const qc = useQueryClient();
  const blank = { bib: '', name: '', division: '' };
  const [f, setF] = useState(blank);
  const [added, setAdded] = useState<Runner[]>([]);
  const bibInput = useRef<HTMLInputElement>(null);
  const add = useMutation({
    mutationFn: () => api<{ runner: Runner }>(`/races/${raceId}/runners`, { body: { bib: f.bib.trim() || null, name: f.name.trim(), division: f.division.trim() || null } }),
    onSuccess: ({ runner }) => {
      setAdded((list) => [runner, ...list].slice(0, 5));
      // Keep the division: walk-ups often come in groups of the same category.
      setF({ ...blank, division: f.division });
      bibInput.current?.focus();
      void qc.invalidateQueries({ queryKey: runnersKey(raceId) });
      void qc.invalidateQueries({ queryKey: liveKey(raceId) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  const field = (flex: string) => (stacked ? undefined : { flex });
  return (
    <div className="stack" style={{ gap: 10 }}>
      <form className={stacked ? 'stack' : 'inline'} style={stacked ? { gap: 10 } : { alignItems: 'flex-end' }} onSubmit={submit}>
        <label className="field" style={field('0 1 110px')}>
          Bib
          <input ref={bibInput} className="input mono" inputMode="numeric" value={f.bib} onChange={(e) => setF({ ...f, bib: e.target.value })} placeholder="Optional" />
        </label>
        <label className="field" style={field('1 1 220px')}>
          Name
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
        </label>
        <label className="field" style={field('0 1 160px')}>
          Division
          <input className="input" value={f.division} onChange={(e) => setF({ ...f, division: e.target.value })} placeholder="e.g. F 30–39" />
        </label>
        <button className="btn primary" type="submit" disabled={add.isPending}>
          {add.isPending ? 'Adding…' : 'Add runner'}
        </button>
      </form>
      {add.error && <div className="notice bad">{(add.error as Error).message}</div>}
      {added.length > 0 && (
        <div className="notice ok" style={{ display: 'block' }}>
          Added {added.map((r) => `${r.bib ? `#${r.bib} ` : ''}${r.name}`).join(', ')}.
        </div>
      )}
    </div>
  );
}
